// Segment ladder (owned by the web:charts agent).
//
// The localisation verdict as a You → LAN → ISP → Region → World ladder. Each
// rung is a card showing loss%, late%, p50/p99 and a small Wilson-interval bar
// (the confidence band on the loss estimate) coloured by severity. The rung the
// verdict blames is highlighted, and the plain-language verdict plus its
// evidence bullets are shown beneath. Because tiers are measured sequentially,
// this is a like-for-like comparison, not a same-instant correlation.

import { useMemo } from 'react';
import type { LadderRung, Tier, Verdict } from '@/engine/types';
import { EmptyNote } from './Canvas';
import {
  readableOn,
  severityColor,
  useChartTheme,
  withAlpha,
  type ChartPalette,
} from './palette';

export interface SegmentLadderProps {
  rungs: LadderRung[];
  verdict: Verdict;
}

const TIER_ORDER: Tier[] = ['lan', 'isp', 'region', 'world'];
const TIER_LABEL: Record<Tier, string> = {
  lan: 'LAN',
  isp: 'ISP',
  region: 'Region',
  world: 'World',
};

export function SegmentLadder({ rungs, verdict }: SegmentLadderProps) {
  const { palette } = useChartTheme();
  const ordered = useMemo(
    () =>
      [...rungs].sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier)),
    [rungs],
  );
  const wilsonMax = useMemo(
    () => Math.max(1, ...ordered.map((r) => r.wilsonHigh), ...ordered.map((r) => r.lossPct)),
    [ordered],
  );

  if (ordered.length === 0) {
    return <EmptyNote label="Run the tiers to build the segment ladder." />;
  }

  return (
    <section aria-label="Segment localisation ladder">
      <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
        <ol
          style={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 0,
            listStyle: 'none',
            margin: 0,
            padding: 0,
            minWidth: 'min-content',
          }}
        >
          <StartCard palette={palette} />
          {ordered.map((r) => (
            <li key={r.tier} style={{ display: 'flex', alignItems: 'stretch' }}>
              <Connector palette={palette} highlight={verdict.tier === r.tier} />
              <RungCard rung={r} wilsonMax={wilsonMax} blamed={verdict.tier === r.tier} palette={palette} />
            </li>
          ))}
        </ol>
      </div>
      <VerdictBlock verdict={verdict} palette={palette} />
    </section>
  );
}

function StartCard({ palette }: { palette: ChartPalette }) {
  return (
    <li style={{ display: 'flex' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: 64,
          padding: '10px 12px',
          borderRadius: 8,
          background: palette.panel,
          border: `1px solid ${palette.grid}`,
          color: palette.text,
          fontWeight: 600,
        }}
      >
        <span aria-hidden style={{ fontSize: 20 }}>
          🖥️
        </span>
        <span style={{ fontSize: 12 }}>You</span>
      </div>
    </li>
  );
}

function Connector({ palette, highlight }: { palette: ChartPalette; highlight: boolean }) {
  return (
    <div
      aria-hidden
      style={{
        alignSelf: 'center',
        width: 26,
        height: 2,
        margin: '0 2px',
        background: highlight ? palette.lostUp : palette.axis,
        position: 'relative',
      }}
    >
      <span
        style={{
          position: 'absolute',
          right: -1,
          top: -3,
          borderTop: '4px solid transparent',
          borderBottom: '4px solid transparent',
          borderLeft: `6px solid ${highlight ? palette.lostUp : palette.axis}`,
        }}
      />
    </div>
  );
}

function RungCard({
  rung,
  wilsonMax,
  blamed,
  palette,
}: {
  rung: LadderRung;
  wilsonMax: number;
  blamed: boolean;
  palette: ChartPalette;
}) {
  const sev = severityColor(palette, rung.lossPct);
  return (
    <div
      style={{
        minWidth: 132,
        padding: '10px 12px',
        borderRadius: 8,
        background: palette.panel,
        border: `2px solid ${blamed ? palette.lostUp : palette.grid}`,
        boxShadow: blamed ? `0 0 0 3px ${withAlpha(palette.lostUp, 0.2)}` : 'none',
        color: palette.text,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>{TIER_LABEL[rung.tier]}</span>
        <span
          style={{
            fontSize: 11,
            padding: '1px 6px',
            borderRadius: 999,
            background: sev,
            color: readableOn(sev),
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {rung.lossPct.toFixed(2)}%
        </span>
      </div>
      <WilsonBar low={rung.wilsonLow} high={rung.wilsonHigh} point={rung.lossPct} max={wilsonMax} palette={palette} />
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          gap: '1px 8px',
          margin: 0,
          fontSize: 11,
          color: palette.textMuted,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <dt>late</dt>
        <dd style={{ margin: 0, textAlign: 'right', color: palette.text }}>{rung.latePct.toFixed(2)}%</dd>
        <dt>p50</dt>
        <dd style={{ margin: 0, textAlign: 'right', color: palette.text }}>{rung.p50.toFixed(1)}</dd>
        <dt>p99</dt>
        <dd style={{ margin: 0, textAlign: 'right', color: palette.text }}>{rung.p99.toFixed(1)}</dd>
      </dl>
    </div>
  );
}

function WilsonBar({
  low,
  high,
  point,
  max,
  palette,
}: {
  low: number;
  high: number;
  point: number;
  max: number;
  palette: ChartPalette;
}) {
  const pct = (v: number) => `${Math.min(100, Math.max(0, (v / max) * 100))}%`;
  const left = Math.min(low, high);
  const width = Math.max(0, Math.abs(high - low));
  return (
    <div
      title={`95% Wilson loss interval: ${low.toFixed(2)}% – ${high.toFixed(2)}%`}
      style={{ position: 'relative', height: 8, background: palette.grid, borderRadius: 4 }}
    >
      <span
        style={{
          position: 'absolute',
          left: pct(left),
          width: pct(width),
          top: 0,
          bottom: 0,
          background: withAlpha(palette.accent, 0.55),
          borderRadius: 4,
        }}
      />
      <span
        style={{
          position: 'absolute',
          left: pct(point),
          top: -2,
          width: 2,
          height: 12,
          background: palette.text,
        }}
      />
    </div>
  );
}

function VerdictBlock({ verdict, palette }: { verdict: Verdict; palette: ChartPalette }) {
  const tierLabel = verdict.tier ? TIER_LABEL[verdict.tier] : null;
  const accent = verdict.tier ? severityColor(palette, 100) : palette.sevOk;
  return (
    <div
      role="status"
      style={{
        marginTop: 12,
        padding: '10px 14px',
        borderRadius: 8,
        borderLeft: `4px solid ${accent}`,
        background: palette.panel,
        color: palette.text,
      }}
    >
      <p style={{ margin: 0, fontWeight: 600 }}>
        {tierLabel ? `Likely fault segment: ${tierLabel}` : 'No clear fault segment'}
      </p>
      <p style={{ margin: '4px 0 0', fontSize: 13 }}>{verdict.text}</p>
      {verdict.evidence.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, color: palette.textMuted }}>
          {verdict.evidence.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
